import { Box, Stack, Typography, Avatar, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";

interface Competitor {
    domain_name: string;
    total_rate: number;
}

interface CompetitorsTooltipProps {
    competitors?: Competitor[];
    onMouseLeave?: () => void;
}

export default function CompetitorsTooltip({ competitors = [], onMouseLeave = () => {} }: CompetitorsTooltipProps) {
    const theme = useTheme();
    const sortedCompetitors = [...competitors].sort((a, b) => b.total_rate - a.total_rate);

    return (
        <Box
            sx={{
                backgroundColor: alpha(theme.palette.grey[700], 0.98),
                borderRadius: '8px',
                boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.2)',
                overflow: 'hidden',
                minWidth: 200,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.common.white,
                        fontSize: '14px',
                        lineHeight: '24px',
                    }}
                >
                    Competitors
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 400,
                        color: alpha(theme.palette.common.white, 0.8),
                        fontSize: '14px',
                        lineHeight: '22px',
                        textAlign: 'right',
                    }}
                >
                    Visibility Rate
                </Typography>
            </Stack>

            {/* Competitors List */}
            <Stack spacing={0}>
                {sortedCompetitors.map((competitor, index) => (
                    <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{
                            px: 1.5,
                            py: 1,
                            backgroundColor: index === 0 
                                ? alpha(theme.palette.grey[500], 0.2)
                                : 'transparent',
                            borderRadius: index === 0 ? '6px' : 0,
                            mx: 0.75,
                            mt: index === 0 ? 0.75 : 0,
                        }}
                    >
                        {/* Competitor Info */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            <Avatar
                                alt={competitor.domain_name}
                                src={`https://www.google.com/s2/favicons?domain=${competitor.domain_name}&sz=32`}
                                sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '12px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.common.white,
                                    fontSize: '14px',
                                    lineHeight: '22px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {competitor.domain_name}
                            </Typography>
                        </Stack>

                        {/* Visibility Rate */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 400,
                                color: alpha(theme.palette.common.white, 0.8),
                                fontSize: '14px',
                                lineHeight: '22px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {fPercent(competitor.total_rate)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}